import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../components/ui/accordion';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { Input } from '../../../../components/ui/input';
import { CheckCircle2 } from 'lucide-react';
import { useCourse } from '../../../../context/useCourseContext';
import { useCourseData } from '../../../../pages/Course';
import AddTopic from './AddTopic';
import DeletedTopic from './DeletedTopic';

function Left() {
  const { isMobile } = useIsMobile();
  const { selectedTopic, setSelectedTopic, setSelectedToggle, expanded } = useCourse();
  const { course } = useCourseData();
  const [searchTerm, setSearchTerm] = useState('');
  const scrollViewportRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Memoize the filtered units to avoid re-calculating on every render
  const filteredUnits = useMemo(() => {
    if (!course?.units) return [];
    if (!searchTerm.trim()) return course.units;

    const lowercasedFilter = searchTerm.toLowerCase();
    return course.units
      .map((unit) => {
        const filteredTopics = unit.topics.filter((topic) => topic.name.toLowerCase().includes(lowercasedFilter));
        return { ...unit, topics: filteredTopics };
      })
      .filter((unit) => unit.topics.length > 0);
  }, [course?.units, searchTerm]);

  const handleTopicClick = (topic) => {
    // Save current scroll position before the state update causes a re-render
    if (scrollViewportRef.current) {
      scrollPositionRef.current = scrollViewportRef.current.scrollTop;
    }
    setSelectedToggle(null);
    setSelectedTopic(topic);
  };

  // Restore scroll position after the component re-renders
  useLayoutEffect(() => {
    if (scrollViewportRef.current) {
      scrollViewportRef.current.scrollTop = scrollPositionRef.current;
    }
  });

  return (
    <div
      className={`
        h-full bg-muted/20 border-r-2 
        transition-all duration-300 ease-in-out
        flex-shrink-0 
        ${expanded ? 'w-full md:w-3/12' : 'w-0 p-0 border-r-0 overflow-hidden'}
      `}>
      <div className="p-2 border-b">
        <Input
          type="search"
          placeholder="🔎 Search topics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-background"
        />
      </div>
      <ScrollArea className="h-[calc(100%-57px)] px-2 pb-1" ref={scrollViewportRef}>
        {filteredUnits.length > 0 ? (
          filteredUnits.map((unit) => (
            <Accordion key={unit?._id} type="single" collapsible>
              <AccordionItem value={`item-${unit.id}`}>
                <AccordionTrigger className="text-md text-left">{unit.title}</AccordionTrigger>
                {unit.topics.map((topic, topicIndex) => (
                  <AccordionContent
                    onClick={() => handleTopicClick(topic)}
                    key={topicIndex}
                    className={`mb-1 p-2 rounded cursor-pointer ${
                      selectedTopic?._id === topic._id
                        ? 'bg-brand text-white '
                        : 'hover:bg-accent hover:text-foreground'
                    } transition-colors duration-100`}>
                    <div className="flex justify-between items-center group">
                      <div className="flex items-center gap-2">
                        {topic.isCompleted && <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />}
                        <h3 className="w-full">{topic?.name}</h3>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <DeletedTopic title={topic} unitId={unit.id} />
                      </div>
                    </div>
                    <p
                      className={`text-xs font-medium mt-1 ${
                        selectedTopic?._id === topic._id ? 'text-white/80' : 'text-muted-foreground'
                      } `}>
                      {topic?.cover}
                    </p>
                  </AccordionContent>
                ))}
                <AccordionContent>
                  <AddTopic unitId={unit._id} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))
        ) : (
          <p className="p-4 text-center text-sm text-muted-foreground">No topics found.</p>
        )}
      </ScrollArea>
    </div>
  );
}

export default Left;
